import { Injectable } from '@angular/core';
import { Selector } from '@ngxs/store';
import _ from 'lodash';
import { CallTagsSelectors } from 'src/app/store/samwin/call-tags/call-tags.selectors';
import { CallTagsState } from 'src/app/store/samwin/call-tags/call-tags.state';
import { CallTagsForSingleCallRemoteDto } from 'src/generated/signalR/call-tags-for-single-call-remote-dto';
import { SingleCallTagBrickSettingsState } from '../../../brick-settings/specific-brick-settings/single-call-tag-brick-settings/single-call-tag-brick-settings.state';

@Injectable()
export class SingleCallTagBehaviourSelectors {
   @Selector([CallTagsSelectors.areCallTagsEditable, SingleCallTagBrickSettingsState.isReadOnly, SingleCallTagBrickSettingsState.isShowCallTagsForActiveCall])
   public static getIsCallTagEditable(
      areCallTagsEditable: boolean,
      isReadOnly: (brickInstanceId: string) => boolean,
      isShowCallTagsForActiveCall: (brickInstanceId: string) => boolean
   ) {
      return (brickInstanceId: string) => {
         if (isReadOnly(brickInstanceId)) {
            return false;
         }
         return isShowCallTagsForActiveCall(brickInstanceId) && areCallTagsEditable;
      };
   }

   @Selector()
   public static getFormattedCallTags() {
      return (CallTags: { [key: string]: string }, callTagId: string) => {
         if (_.isNil(CallTags)) {
            return null;
         }
         return CallTags[callTagId];
      };
   }

   @Selector([CallTagsSelectors.callTagsForActiveOrRingingCall, SingleCallTagBrickSettingsState.isShowCallTagsForActiveCall])
   public static canDisplayActiveCallTag(
      callTagsActiveCall: CallTagsForSingleCallRemoteDto,
      isShowCallTagsForActiveCall: (brickInstanceId: string) => boolean
   ) {
      return (brickInstanceId: string) => {
         return !_.isNil(callTagsActiveCall) && isShowCallTagsForActiveCall(brickInstanceId);
      };
   }

   @Selector([
      CallTagsSelectors.callTagsForActiveOrRingingCall,
      SingleCallTagBrickSettingsState.getCallTagId,
      SingleCallTagBehaviourSelectors.getFormattedCallTags,
   ])
   public static getActiveCallTagValue(
      callTagsActiveCall: CallTagsForSingleCallRemoteDto,
      getCallTagId: (brickInstanceId: string) => string,
      getFormattedCallTags: (
         CallTags: {
            [key: string]: string;
         },
         callTagId: string
      ) => string
   ) {
      return (brickInstanceId: string) => {
         return getFormattedCallTags(callTagsActiveCall.callTags, getCallTagId(brickInstanceId));
      };
   }

   @Selector([CallTagsState.lastSelectedHistoryId, SingleCallTagBrickSettingsState.isShowCallTagsForHistoryCalls])
   public static canDisplayHistoryCallTag(currentUniqueCallIdForHistoryCall: string, isShowCallTagsForHistoryCalls: (brickInstanceId: string) => boolean) {
      return (brickInstanceId: string) => {
         return !_.isNil(currentUniqueCallIdForHistoryCall) && isShowCallTagsForHistoryCalls(brickInstanceId);
      };
   }

   @Selector([CallTagsState.callTagsForSelectedHistoryCall, SingleCallTagBrickSettingsState.getCallTagId, SingleCallTagBehaviourSelectors.getFormattedCallTags])
   public static getHistoryCallTagValue(
      historyCallTags: {
         [key: string]: string;
      },
      getCallTagId: (brickInstanceId: string) => string,
      getFormattedCallTags: (
         CallTags: {
            [key: string]: string;
         },
         callTagId: string
      ) => string
   ) {
      return (brickInstanceId: string) => {
         return getFormattedCallTags(historyCallTags, getCallTagId(brickInstanceId));
      };
   }

   @Selector([
      CallTagsSelectors.areCallTagsEditable,
      SingleCallTagBehaviourSelectors.canDisplayActiveCallTag,
      SingleCallTagBehaviourSelectors.canDisplayHistoryCallTag,
      SingleCallTagBehaviourSelectors.getActiveCallTagValue,
      SingleCallTagBehaviourSelectors.getHistoryCallTagValue,
   ])
   public static getCallTagValue(
      areCallTagsEditable: boolean,
      canDisplayActiveCallTag: (brickInstanceId: string) => boolean,
      canDisplayHistoryCallTag: (brickInstanceId: string) => boolean,
      getActiveCallTagValue: (brickInstanceId: string) => string,
      getHistoryCallTagValue: (brickInstanceId: string) => string
   ) {
      return (brickInstanceId: string) => {
         const canDisplayActive = canDisplayActiveCallTag(brickInstanceId);
         const canDisplayHistory = canDisplayHistoryCallTag(brickInstanceId);
         if (!canDisplayActive && !canDisplayHistory) {
            return;
         }
         if (areCallTagsEditable && canDisplayActive) {
            return getActiveCallTagValue(brickInstanceId);
         }
         if (!areCallTagsEditable && canDisplayHistory) {
            return getHistoryCallTagValue(brickInstanceId);
         }

         return;
      };
   }
}
